import WarningIcon from '@mui/icons-material/Warning'
import { Box, Stack, Typography, useTheme } from '@mui/material'
import { useNavigate, useRouterState } from '@tanstack/react-router'
import fontColorContrast from 'font-color-contrast'
import { useNavigatorContext } from 'src/contexts/NavigatorContext'
import { Direction } from 'src/generated/brezavta/requests/types.gen'
import { Leg } from 'src/generated/brezavta/requests/types.gen'
import { getColorForMode, getIconForMode } from 'src/lib/modes'
import { getDirectionIcon, isSignificantDirection } from 'src/lib/directions'
import { TRANSIT_MODES } from 'src/lib/navigationUtils'
import { formatMillisTime } from 'src/lib/util'

export default function NavigatorBubble() {
    const theme = useTheme()
    const { navState } = useNavigatorContext()
    const navigate = useNavigate()
    const path = useRouterState({ select: r => r.location.pathname })

    if (!navState || path === '/plan/navigate') return null

    const { plan, legIndex, delaySeconds, transferStatus, stopsRemaining, nextWalkStep } = navState
    const leg = plan.legs[legIndex]
    if (!leg) return null

    const isTransit = TRANSIT_MODES.includes(leg.mode)
    const steps = leg.steps ?? []
    const distM = steps.reduce((s, step) => s + step.distance, 0)
    const distStr = distM < 1000 ? `${Math.round(distM)}m` : `${(distM / 1000).toFixed(1)}km`

    // Use position-aware next step from navState, fall back to first significant step
    const effectiveNextStep = nextWalkStep
        ?? steps.find(step => step.relative_direction && isSignificantDirection(step.relative_direction as Direction))
    const nextTurnDirection = effectiveNextStep?.relative_direction as Direction | undefined
    const nextTurnDistance = effectiveNextStep?.distance ?? 0
    const nextTurnDistStr = nextTurnDistance < 1000
        ? `${Math.round(nextTurnDistance)}m`
        : `${(nextTurnDistance / 1000).toFixed(1)}km`

    const isWarning = transferStatus === 'at-risk' || transferStatus === 'missed'
    const bgColor = isWarning
        ? theme.palette.error.main
        : isTransit
            ? (getColorForMode(leg) ?? theme.palette.primary.main)
            : theme.palette.primary.main

    const textColor = fontColorContrast(bgColor)
    const adjustedArrival = leg.to_point.arrival_time + (delaySeconds ?? 0) * 1000

    return (
        <Box
            onClick={() => navigate({ to: '/plan/navigate' })}
            sx={{
                'position': 'fixed',
                'top': 'calc(env(safe-area-inset-top, 0px) + 12px)',
                'left': 12,
                '@media (orientation: landscape)': {
                    left: 'auto',
                    right: 12,
                },
                'bgcolor': bgColor,
                'color': textColor,
                'borderRadius': '999px',
                'px': 1.5,
                'py': 1,
                'cursor': 'pointer',
                'zIndex': 2000,
                'userSelect': 'none',
                'boxShadow': '0 2px 12px rgba(0,0,0,0.2)',
                '&:active': { transform: 'scale(0.97)' },
                'transition': 'transform 0.1s, background-color 0.3s',
            }}
        >
            {isTransit
                ? (
                        <TransitContent
                            leg={leg}
                            stopsRemaining={stopsRemaining ?? null}
                            adjustedArrival={adjustedArrival}
                            isWarning={isWarning}
                            textColor={textColor}
                        />
                    )
                : (
                        <WalkContent
                            leg={leg}
                            distStr={distStr}
                            nextTurnDirection={nextTurnDirection}
                            nextTurnDistStr={nextTurnDistStr}
                            textColor={textColor}
                        />
                    )}
        </Box>
    )
}

interface TransitContentProps {
    leg: Leg
    stopsRemaining: number | null
    adjustedArrival: number
    isWarning: boolean
    textColor: string
}

function TransitContent({ leg, stopsRemaining, adjustedArrival, isWarning, textColor }: TransitContentProps) {
    const routeColor = getColorForMode(leg) ?? '#000'
    const routeTextColor = leg.trip?.text_color ?? fontColorContrast(routeColor)

    return (
        <Stack direction="row" alignItems="center" gap={1}>
            <Box sx={{
                bgcolor: routeColor,
                color: routeTextColor,
                borderRadius: '6px',
                px: 0.75,
                py: 0.25,
                fontWeight: 700,
                fontSize: '13px',
                lineHeight: 1.4,
                whiteSpace: 'nowrap',
            }}
            >
                {leg.trip?.short_name}
            </Box>
            <Typography sx={{ fontWeight: 700, fontSize: '15px', lineHeight: 1, color: textColor, whiteSpace: 'nowrap' }}>
                {stopsRemaining != null && stopsRemaining > 0
                    ? `${stopsRemaining} ●`
                    : formatMillisTime(adjustedArrival)}
            </Typography>
            {isWarning && <WarningIcon sx={{ fontSize: 16, color: textColor }} />}
        </Stack>
    )
}

interface WalkContentProps {
    leg: Leg
    distStr: string
    nextTurnDirection: Direction | undefined
    nextTurnDistStr: string
    textColor: string
}

function WalkContent({ leg, distStr, nextTurnDirection, nextTurnDistStr, textColor }: WalkContentProps) {
    return (
        <Stack direction="row" alignItems="center" gap={0.75}>
            <Box sx={{ color: textColor, display: 'flex', alignItems: 'center' }}>
                {getIconForMode(leg)}
            </Box>
            <Typography sx={{ fontWeight: 600, fontSize: '14px', color: textColor, whiteSpace: 'nowrap' }}>
                {distStr}
            </Typography>
            {nextTurnDirection && (
                <Stack direction="row" alignItems="center" gap={0.25}>
                    <Typography sx={{ fontWeight: 700, fontSize: '16px', color: textColor, lineHeight: 1 }}>
                        {getDirectionIcon(nextTurnDirection)}
                    </Typography>
                    <Typography sx={{ fontWeight: 500, fontSize: '12px', color: textColor }}>
                        {nextTurnDistStr}
                    </Typography>
                </Stack>
            )}
        </Stack>
    )
}
